import type { ComponentType } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthGate } from '@/components/auth/auth-gate';
import { DefaultLayout } from '@/layouts/default';
import { NAV_GROUPS } from '@/layouts/nav';
import UserNotificationsPage from '@/pages/communications/user-notifications';
import AbuseMonitoringPage from '@/pages/compliance/abuse';
import AbuseSubjectPage from '@/pages/compliance/abuse-subject';
import Dac8Page from '@/pages/compliance/dac8';
import Dac8AuditViewerPage from '@/pages/compliance/dac8-audit-viewer';
import FiuUtrPage from '@/pages/compliance/fiu-utr';
import InstitutionsPage from '@/pages/compliance/institutions';
import JurisdictionsPage from '@/pages/compliance/jurisdictions';
import MarketSurveillancePage from '@/pages/compliance/market-surveillance';
import MarketSurveillanceAlertPage from '@/pages/compliance/market-surveillance-alert';
import OnboardingReviewsPage from '@/pages/compliance/onboarding-reviews';
import ReportViewerPage from '@/pages/compliance/report-viewer';
import ReportsPage from '@/pages/compliance/reports';
import TermsAndConditionsPage from '@/pages/compliance/terms-and-conditions';
import WhitelistedPayoutAddressesPage from '@/pages/compliance/whitelisted-payout-addresses';
import ExchangePage from '@/pages/devops/exchange';
import QuestdbPage from '@/pages/devops/questdb';
import HomePage from '@/pages/home';
import InstantDashboardPage from '@/pages/instant/dashboard';
import NotFoundPage from '@/pages/not-found';
import AccountCleanupPage from '@/pages/operations/account-cleanup';
import AccountCleanupReviewPage from '@/pages/operations/account-cleanup-review';
import AccountCleanupViewerPage from '@/pages/operations/account-cleanup-viewer';
import BrokerBatchPage from '@/pages/operations/broker-batch';
import BrokerDetailPage from '@/pages/operations/broker-detail';
import BrokersPage from '@/pages/operations/brokers';
import ClientCategorisationPage from '@/pages/operations/client-categorisation';
import CreditLinesPage from '@/pages/operations/credit-lines';
import FeeRunDetailPage from '@/pages/operations/fee-run-detail';
import FeeRunsPage from '@/pages/operations/fee-runs';
import FeesPage from '@/pages/operations/fees';
import MarketMakerPage from '@/pages/operations/market-maker';
import MarketsPage from '@/pages/operations/markets';
import MmProgramDetailPage from '@/pages/operations/mm-program-detail';
import MmProgramsPage from '@/pages/operations/mm-programs';
import ParkedDepositsPage from '@/pages/operations/parked-deposits';
import PayoutAccountsPage from '@/pages/operations/payout-accounts';
import TradingPage from '@/pages/operations/trading';
import TransactionsPage from '@/pages/operations/transactions';
import UserAuditPage from '@/pages/operations/user-audit';
import UserAuditViewerPage from '@/pages/operations/user-audit-viewer';
import UserDetailPage from '@/pages/operations/user-detail';
import UserReferencesPage from '@/pages/operations/user-references';
import UserSearchPage from '@/pages/operations/user-search';
import PlaceholderPage from '@/pages/placeholder';
import AssetsPage from '@/pages/risk/assets';
import RiskFeesPage from '@/pages/risk/fees';
import FxSwapsPage from '@/pages/risk/fx-swaps';
import SignedOutPage from '@/pages/signed-out';
import AssetFlowsDashboardPage from '@/pages/treasury/flows/flows-dashboard';
import FrickBalancesPage from '@/pages/treasury/frick/balances';
import KinexysAccountsPage from '@/pages/treasury/kx/accounts';

// Ported pages register here; everything else renders a placeholder until its phase
const PAGES: Record<string, ComponentType> = {
  '/communications/user-notifications': UserNotificationsPage,
  '/compliance/dac8': Dac8Page,
  '/compliance/fiu-utr': FiuUtrPage,
  '/compliance/institutions': InstitutionsPage,
  '/compliance/jurisdictions': JurisdictionsPage,
  '/compliance/market-surveillance': MarketSurveillancePage,
  '/compliance/abuse': AbuseMonitoringPage,
  '/compliance/onboarding-reviews': OnboardingReviewsPage,
  '/compliance/reports': ReportsPage,
  '/compliance/terms-and-conditions': TermsAndConditionsPage,
  '/compliance/whitelisted-payout-addresses': WhitelistedPayoutAddressesPage,
  '/devops/exchange': ExchangePage,
  '/devops/questdb': QuestdbPage,
  '/instant/dashboard': InstantDashboardPage,
  '/operations/brokers': BrokersPage,
  '/operations/client-categorisation': ClientCategorisationPage,
  '/operations/credit-lines': CreditLinesPage,
  '/operations/fee-runs': FeeRunsPage,
  '/operations/fees': FeesPage,
  '/operations/market-maker': MarketMakerPage,
  '/operations/markets': MarketsPage,
  '/operations/mm-programs': MmProgramsPage,
  '/operations/parked-deposits': ParkedDepositsPage,
  '/operations/payout-accounts': PayoutAccountsPage,
  '/operations/trading': TradingPage,
  '/operations/transactions': TransactionsPage,
  '/operations/user-audit': UserAuditPage,
  '/operations/user-references': UserReferencesPage,
  '/operations/user-search': UserSearchPage,
  '/risk/assets': AssetsPage,
  '/risk/fees': RiskFeesPage,
  '/risk/fx-swaps': FxSwapsPage,
  '/treasury/flows/dashboard': AssetFlowsDashboardPage,
  '/treasury/frick/balances': FrickBalancesPage,
  '/treasury/kx/accounts': KinexysAccountsPage,
};

// Section index routes redirect like the legacy app (placeholder pages folded away)
const SECTION_REDIRECTS: Record<string, string> = {
  '/communications': '/communications/user-notifications',
  '/operations': '/operations/user-search',
  '/devops': '/devops/exchange',
  '/compliance': '/compliance/institutions',
  '/risk': '/risk/assets',
  '/instant': '/instant/dashboard',
  '/treasury': '/treasury/flows/dashboard',
  '/treasury/flows': '/treasury/flows/dashboard',
  '/treasury/frick': '/treasury/frick/balances',
  '/treasury/kx': '/treasury/kx/accounts',
};

const UserTabRedirect = ({ tab }: { tab: string }) => {
  const { pid } = useParams();
  return <Navigate replace to={{ pathname: `/operations/users/${pid}`, search: `?tab=${tab}` }} />;
};
UserTabRedirect.displayName = 'UserTabRedirect';

const Routing = () => (
  <Routes>
    {/* Outside the gate: inside it, the live Okta session would sign the user straight back in */}
    <Route element={<SignedOutPage />} path="/signed-out" />
    <Route element={<AuthGate />}>
      <Route element={<DefaultLayout />}>
        <Route element={<HomePage />} path="/" />
        {NAV_GROUPS.flatMap((group) => group.items).map((item) => {
          const Page = PAGES[item.to];
          return <Route element={Page ? <Page /> : <PlaceholderPage title={item.title} />} key={item.to} path={item.to} />;
        })}
        {/* Detail pages reached via links, not the nav */}
        <Route element={<UserDetailPage />} path="/operations/users/:pid" />
        {/* Legacy deep-link stubs preserved: /kyc and /pii land on the matching tab.
            NOTE: `..` in <Navigate> resolves by ROUTE tree (parent = layout), not URL
            segments — an absolute target built from the param is required. */}
        <Route element={<UserTabRedirect tab="kyc" />} path="/operations/users/:pid/kyc" />
        <Route element={<UserTabRedirect tab="pii" />} path="/operations/users/:pid/pii" />
        <Route element={<FeeRunDetailPage />} path="/operations/fee-runs/:runId" />
        <Route element={<BrokerDetailPage />} path="/operations/brokers/:brokerId" />
        <Route element={<BrokerBatchPage />} path="/operations/brokers/:brokerId/batches/:batchId" />
        <Route element={<MmProgramDetailPage />} path="/operations/mm-programs/:pid" />
        <Route element={<ReportViewerPage />} path="/compliance/reports/view" />
        <Route element={<Dac8AuditViewerPage />} path="/compliance/dac8/audit/view" />
        <Route element={<UserAuditViewerPage />} path="/operations/user-audit/view" />
        <Route element={<AccountCleanupPage />} path="/operations/user-audit/cleanup" />
        <Route element={<AccountCleanupReviewPage />} path="/operations/user-audit/cleanup/review" />
        <Route element={<AccountCleanupViewerPage />} path="/operations/user-audit/cleanup/view" />
        <Route element={<MarketSurveillanceAlertPage />} path="/compliance/market-surveillance/:alertId" />
        <Route element={<AbuseSubjectPage />} path="/compliance/abuse/subject" />
        {Object.entries(SECTION_REDIRECTS).map(([from, to]) => (
          <Route element={<Navigate replace to={to} />} key={from} path={from} />
        ))}
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Route>
  </Routes>
);
Routing.displayName = 'Routing';

export default Routing;
